import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, BookOpenText, Gem, Package, Search, ShieldCheck } from 'lucide-react'
import { api } from '@/lib/api'
import { localizedName, slugify } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import { trackSearch } from '@/lib/analytics'
import { ImageCarousel, type CarouselSlide } from '@/components/ImageCarousel'
import { AutoscrollSlider } from '@/components/AutoscrollSlider'
import { YouTubeCarousel } from '@/components/YouTubeCarousel'
import type { ProductSummary, StaticPage, FilterFacets, BannerSlideOut } from '@/shared/types'
import brandLogosMap from './brand_logos_map.json'

interface PublicSettings {
  quiz_enabled: boolean
  shop_name: string
  brand_logo_url?: string | null
  youtube_channel_id?: string
}

const FALLBACK_HERO_SLIDES: CarouselSlide[] = [
  {
    imageUrl: '/app/assets/banners/banner-review-voucher.webp',
    alt: 'Отримай 200 грн за відгук',
    href: '/catalog',
  },
  {
    imageUrl: '/app/assets/banners/banner-devils-intrigue.webp',
    alt: "Devil's Intrigue",
    href: '/catalog/devils-intrigue',
  },
  {
    imageUrl: '/app/assets/banners/banner-notre-dame.webp',
    alt: 'Notre Dame — Notte di Natale',
    href: '/catalog/notre-dame-notte-di-natale',
  },
  {
    imageUrl: '/app/assets/banners/banner-honour-43.webp',
    alt: 'Honour 43 Woman',
    href: '/catalog/honour-43-woman',
  },
  {
    imageUrl: '/app/assets/banners/banner-clive-christian.webp',
    alt: 'Clive Christian — Strange Heavens Out of the Blue',
    href: '/catalog/strange-heavens-out-of-the-blue',
  },
]

const DEFAULT_BRANDS = [
  'Acqua di Parma',
  'Amouage',
  'BDK Parfums',
  'Creed',
  'Diptyque',
  'Maison Crivelli',
  'Montale',
  'Tiziana Terenzi',
]

export function HomePage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('home')
  const navigate = useNavigate()
  
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const { data: dbBanners } = useQuery<BannerSlideOut[]>({
    queryKey: ['banners'],
    queryFn: () => api.get<BannerSlideOut[]>('/banners'),
    staleTime: 300_000,
  })

  const heroSlides: CarouselSlide[] = useMemo(() => {
    if (dbBanners && dbBanners.length > 0) {
      return dbBanners.map((b) => ({
        imageUrl: b.image_url,
        alt: b.alt_text || 'Banner',
        href: b.href || '/catalog',
      }))
    }
    return FALLBACK_HERO_SLIDES
  }, [dbBanners])

  const { data: products } = useQuery<ProductSummary[]>({
    queryKey: ['products', 'home-new'],
    queryFn: () => api.get<ProductSummary[]>('/products'),
  })

  const { data: facets } = useQuery<FilterFacets>({
    queryKey: ['product-filter-facets'],
    queryFn: () => api.get<FilterFacets>('/products/filter-facets'),
    staleTime: 300_000,
  })

  const brands = useMemo(() => {
    const brandSpec = facets?.specs.find((s) => s.code === 'brand' || s.name_uk === 'Бренд')
    return brandSpec?.options || DEFAULT_BRANDS
  }, [facets])

  const { data: suggestions } = useQuery<{
    products: Array<{ id: number; name: string; price_uah: number; photo_url: string; category_name?: string; slug?: string }>
    brands: string[]
    categories: string[]
  }>({
    queryKey: ['search-suggestions', searchQuery],
    queryFn: () => api.get(`/products/search-suggestions?q=${encodeURIComponent(searchQuery.trim())}`),
    enabled: searchQuery.trim().length >= 2,
    staleTime: 30_000,
  })

  const filteredResults = useMemo(() => {
    if (!suggestions?.products) return []
    return suggestions.products.map((p) => ({
      id: p.id,
      name_uk: p.name,
      name_en: p.name,
      price_uah: p.price_uah,
      price_usd: p.price_uah,
      photo_url: p.photo_url,
      category_name_uk: p.category_name,
      category_name_en: p.category_name,
      slug: p.slug || String(p.id),
      in_stock: true,
      stock_qty: null,
      is_made_to_order: false,
      production_days: 0,
      category_id: null,
      store_id: null,
      is_kit: false,
    }))
  }, [suggestions])

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      setIsOpen(true) // eslint-disable-line react-hooks/set-state-in-effect
      setHighlightedIndex(-1)
    } else {
      setIsOpen(false)
    }
  }, [searchQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      trackSearch(searchQuery.trim())
      navigate({ to: '/catalog', search: { q: searchQuery.trim() } })
      setIsOpen(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredResults.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev < filteredResults.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredResults.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < filteredResults.length) {
        const prod = filteredResults[highlightedIndex]
        navigate({ to: '/catalog/$productSlug', params: { productSlug: prod.slug || String(prod.id) } })
        setIsOpen(false)
      } else if (searchQuery.trim()) {
        navigate({ to: '/catalog', search: { q: searchQuery.trim() } })
        setIsOpen(false)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const { data: pages } = useQuery<StaticPage[]>({
    queryKey: ['pages'],
    queryFn: () => api.get<StaticPage[]>('/pages'),
    staleTime: 300_000,
  })

  const newest = (products ?? []).slice(0, 10)
  const aromoboxes = (products ?? []).filter((p) => p.is_kit).slice(0, 10)
  const aromoboxesCategoryId = (products ?? []).find((p) => p.is_kit)?.category_id
  const trust = Array.isArray(T.mayparfumTrust) ? T.mayparfumTrust : []
  const highlights = Array.isArray(T.mayparfumHighlights) ? T.mayparfumHighlights : []
  const logoSrc = '/app/assets/mayparfum-logo-dark.jpg'

  const isMayParfum = (settings?.shop_name ?? '').toLowerCase().replace(/\s+/g, '').includes('mayparfum')
  const richTitle = isMayParfum
    ? (lang === 'en' ? 'MayParfum — Original Niche Perfumery' : 'MayParfum — оригінальна нішева парфумерія')
    : settings?.shop_name
  usePageTitle(richTitle)

  return (
    <div className="min-h-screen bg-[#3a4352] pb-20 text-[#f8f3ea]">
      <div className="relative overflow-hidden bg-[#3a4352]">
        {/* Background gradient covering header, search, and welcome hero */}
        <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(188,144,80,0.18),transparent_50%),linear-gradient(180deg,rgba(58,67,82,0)_0%,rgba(28,23,20,0.4)_100%)] pointer-events-none" />

        <div className="relative z-10">
          {/* BRAND HEADER BAR */}
          <div className="mx-auto max-w-2xl px-5 pt-4 pb-1 flex items-center justify-between">
            <Link to="/">
              <img
                src={settings?.brand_logo_url || '/app/assets/mayparfum-logo-dark.jpg'}
                alt="MAY PARFUM"
                className="h-10 max-w-[220px] object-contain rounded-xl shadow-md transition hover:opacity-90"
              />
            </Link>
          </div>

          {heroSlides.length > 0 && (
            <ImageCarousel slides={heroSlides} aspectClassName="aspect-[1640/624] h-auto w-full" />
          )}

      {/* SEARCH WIDGET */}
      <div className="relative mx-auto max-w-2xl px-5 pt-6 pb-2" ref={containerRef}>
        <form onSubmit={handleSearchSubmit} className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchQuery.trim().length >= 2) setIsOpen(true)
            }}
            onKeyDown={handleKeyDown}
            placeholder={lang === 'en' ? 'Search fragrances, brands, notes...' : lang === 'ru' ? 'Поиск ароматов, брендов, нот...' : 'Пошук ароматів, брендів, нот...'}
            className="w-full rounded-2xl border border-[#6f5840] bg-[#efe3d1]/10 py-3 pl-11 pr-24 text-sm text-white placeholder-[#b9aa99] focus:outline-none focus:ring-2 focus:ring-[#bc9050]"
          />
          <Search className="absolute left-4 top-3.5 h-4 w-4 text-[#b9aa99]" />
          <button
            type="submit"
            className="absolute right-2.5 top-2 rounded-xl bg-[#bc9050] px-3.5 py-1.5 text-xs font-semibold text-[#17120f] hover:bg-[#bc9050]/90 transition"
          >
            {lang === 'en' ? 'Find' : lang === 'ru' ? 'Найти' : 'Знайти'}
          </button>
        </form>

        {/* Dynamic Search Dropdown */}
        {isOpen && (
          <div className="absolute left-5 right-5 md:left-0 md:right-0 z-50 mt-1 max-h-80 overflow-y-auto rounded-2xl border border-[#6f5840] bg-[#3a4352] shadow-2xl backdrop-blur-md">
            {filteredResults.length > 0 ? (
              <>
                {filteredResults.slice(0, 5).map((product, idx) => {
                  const name = localizedName(lang, product.name_uk, product.name_en)
                  return (
                    <Link
                      key={product.id}
                      to="/catalog/$productSlug"
                      params={{ productSlug: product.slug || String(product.id) }}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 border-b border-[#6f5840]/30 transition hover:bg-[#efe3d1]/10 ${
                        idx === highlightedIndex ? 'bg-[#efe3d1]/10' : ''
                      }`}
                    >
                      {product.photo_url ? (
                        <img
                          src={product.photo_url}
                          alt={name}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#efe3d1]/10">
                          <Package className="h-5 w-5 text-[#b9aa99]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-white">{name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-[#d7b171]">{product.price_uah} UAH</p>
                      </div>
                    </Link>
                  )
                })}
                <Link
                  to="/catalog"
                  search={{ q: searchQuery }}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-[#d7b171] hover:bg-[#efe3d1]/10 transition"
                >
                  {lang === 'en' ? 'Show all results' : lang === 'ru' ? 'Показать все результаты' : 'Показати всі результати'}
                </Link>
              </>
            ) : (
              <div className="p-4 text-center text-xs text-[#b9aa99]">
                {lang === 'en' ? 'No results found' : lang === 'ru' ? 'Ничего не найдено' : 'Нічого не знайдено'}
              </div>
            )}
          </div>
        )}
      </div>

      <section className="relative text-[#f8f3ea]">
        <div className="relative mx-auto grid max-w-6xl gap-8 px-5 pb-8 pt-7 md:grid-cols-[0.95fr_1.05fr] md:px-8 md:pb-10">
          <div className="flex min-h-[340px] flex-col justify-end md:min-h-[500px]">
            <img
              src={logoSrc}
              alt={T.mayparfumTitle}
              className="mb-8 h-auto w-48 max-w-[72vw] object-contain md:w-64 mix-blend-screen"
            />
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d7b171]">
              {T.mayparfumEyebrow}
            </p>
            <h1 className="mt-4 text-4xl font-semibold leading-none text-white md:text-6xl">
              {T.mayparfumTitle}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-[#e4d6c4] md:text-lg">
              {T.mayparfumSubtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/catalog"
                className="inline-flex items-center gap-2 rounded-full bg-[#bc9050] px-5 py-3 text-sm font-semibold text-[#17120f]"
              >
                {T.mayparfumPrimary}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/stories"
                className="inline-flex items-center gap-2 rounded-full border border-[#6f5840] px-5 py-3 text-sm font-semibold text-[#f7efe3]"
              >
                <BookOpenText className="h-4 w-4" />
                {T.mayparfumSecondary}
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-2 text-center text-[11px] font-semibold uppercase tracking-wide text-[#d7b171] md:max-w-lg">
              {trust.map((item) => (
                <span key={item} className="border border-[#4c3b2b] bg-[#211915] px-2 py-2">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="w-full overflow-hidden md:max-w-[48vw] md:self-end md:pb-6">
            <AutoscrollSlider>
              {newest.slice(0, 6).map((product, index) => {
                const name = localizedName(lang, product.name_uk, product.name_en)
                return (
                  <Link
                    key={product.id}
                    to="/catalog/$productSlug"
                    params={{ productSlug: product.slug || String(product.id) }}
                    className={`group block overflow-hidden border border-[#657086] bg-[#f8f3ea] text-[#211915] shadow-[0_20px_60px_rgba(0,0,0,0.22)] w-[230px] md:w-[280px] ${
                      index === 0 ? 'md:w-[360px]' : ''
                    }`}
                  >
                    {product.photo_url ? (
                      <img
                        src={product.photo_url}
                        alt={name}
                        className={`${index === 0 ? 'h-72 md:h-80' : 'h-56 md:h-72'} w-full object-cover transition group-hover:scale-[1.02]`}
                      />
                    ) : (
                      <div className={`${index === 0 ? 'h-72 md:h-80' : 'h-56 md:h-72'} flex w-full items-center justify-center bg-[#efe3d1]`}>
                        <Package className="h-8 w-8 text-[#6f5840]" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="line-clamp-2 text-sm font-semibold text-[#211915]">{name}</p>
                      <p className="mt-1 text-sm font-bold text-[#9d733a]">{product.price_uah} UAH</p>
                    </div>
                  </Link>
                )
              })}
            </AutoscrollSlider>
          </div>
        </div>
      </section>
      </div>
      </div>

      <section className="border-y border-[#657086] bg-[#f8f3ea] text-[#211915]">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 md:grid-cols-[0.95fr_1.05fr] md:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9d733a]">
              {T.mayparfumWelcomeEyebrow}
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[#211915]">
              {T.mayparfumWelcomeTitle}
            </h2>
          </div>
          <p className="text-sm leading-7 text-[#5b5147] md:text-base">
            {T.mayparfumWelcomeBody}
          </p>
        </div>
      </section>

      {/* QUICK CHOICE BY GENDER */}
      <section className="mx-auto max-w-6xl px-5 py-8 md:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d7b171]">
          {lang === 'en' ? 'SHOP BY GENDER' : 'АРОМАТИ ЗА СТАТТЮ'}
        </p>
        <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-3">
          <Link
            to="/catalog"
            search={{ attr: 'hender', value: 'Жіночі (Фемінні)' }}
            className="group relative h-48 overflow-hidden border border-[#657086] bg-black shadow-md md:h-56"
          >
            <img
              src="/app/assets/gender_women.png"
              alt="For Her"
              className="absolute inset-0 h-full w-full object-cover opacity-75 transition-all duration-500 group-hover:scale-105 group-hover:opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
              <span className="text-sm font-bold uppercase tracking-wider md:text-base">
                {lang === 'en' ? 'For Her' : 'Для неї'}
              </span>
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </div>
          </Link>

          <Link
            to="/catalog"
            search={{ attr: 'hender', value: 'Чоловічі (Мускульні)' }}
            className="group relative h-48 overflow-hidden border border-[#657086] bg-black shadow-md md:h-56"
          >
            <img
              src="/app/assets/gender_men.png"
              alt="For Him"
              className="absolute inset-0 h-full w-full object-cover opacity-75 transition-all duration-500 group-hover:scale-105 group-hover:opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
              <span className="text-sm font-bold uppercase tracking-wider md:text-base">
                {lang === 'en' ? 'For Him' : 'Для нього'}
              </span>
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </div>
          </Link>

          <Link
            to="/catalog"
            search={{ attr: 'hender', value: 'Унісекс' }}
            className="group relative h-48 overflow-hidden border border-[#657086] bg-black shadow-md md:h-56"
          >
            <img
              src="/app/assets/gender_unisex.png"
              alt="Unisex"
              className="absolute inset-0 h-full w-full object-cover opacity-75 transition-all duration-500 group-hover:scale-105 group-hover:opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
              <span className="text-sm font-bold uppercase tracking-wider md:text-base">
                {lang === 'en' ? 'Unisex' : 'Унісекс'}
              </span>
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </div>
          </Link>
        </div>
      </section>

      {settings?.quiz_enabled && (
        <section className="mx-auto max-w-6xl px-5 py-2 md:px-8">
          <Link
            to="/quiz"
            className="group block border border-[#6f5840] overflow-hidden rounded-2xl shadow-md transition-all duration-300 hover:scale-[1.008] hover:border-[#bc9050]/60 hover:shadow-lg hover:shadow-black/40"
          >
            <img
              src="/app/assets/quiz_banner.webp"
              alt={lang === 'en' ? 'Fragrance Quiz — Find your perfect scent' : 'Парфумерний квіз — Підбери свій ідеальний аромат'}
              className="w-full h-auto object-cover block opacity-95 group-hover:opacity-100 transition-opacity"
              loading="lazy"
            />
          </Link>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-5 py-8 md:px-8">
        <div className="mb-4 flex items-end justify-between gap-4">
          <h2 className="text-xl font-semibold text-[#f8f3ea]">
            {lang === 'en' ? 'Brands' : 'Бренди'}
          </h2>
          <Link to="/brands" className="inline-flex items-center gap-1 text-sm font-semibold text-[#9d733a] hover:text-[#c9a24b] transition-colors">
            {lang === 'en' ? 'All brands' : lang === 'ru' ? 'Все бренды' : 'Всі бренди'}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="w-full overflow-hidden">
          <AutoscrollSlider>
            {brands.map((brand) => {
              const cleanSlug = brand.toLowerCase().replace(/ /g, '_')
              const brandSlug = slugify(brand)
              const matchedBrand = brandLogosMap.find((item) => item.slug === cleanSlug)
              const ext = matchedBrand ? matchedBrand.ext : 'jpg'
              const imgPath = `/app/assets/brands/${cleanSlug}.${ext}`
              const hasLogo = !!matchedBrand

              return (
                <Link
                  key={brand}
                  to="/brands/$brandSlug"
                  params={{ brandSlug }}
                  className="group block overflow-hidden border border-[#657086] bg-[#f8f3ea] w-[200px] md:w-[250px] h-56 md:h-64 flex flex-col justify-between transition-transform duration-300 hover:scale-[1.02] shadow-sm hover:shadow-md"
                >
                  <div className="h-40 w-full overflow-hidden bg-white flex items-center justify-center p-4 md:h-48">
                    {hasLogo ? (
                      <img
                        src={imgPath}
                        alt={brand}
                        className="max-h-full max-w-full object-contain transition duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#3a4352]/10 p-6 text-center border-b border-[#657086]/20">
                        <span className="font-serif text-lg font-bold tracking-widest text-[#bc9050] uppercase transition duration-500 group-hover:scale-105">
                          {brand}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#211915] bg-[#f8f3ea] border-t border-[#657086]/20 group-hover:text-[#9d733a] transition-colors">
                    {brand}
                  </p>
                </Link>
              )
            })}
          </AutoscrollSlider>
        </div>
      </section>

      {aromoboxes.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 py-8 md:px-8">
          <div className="mb-4 flex items-end justify-between gap-4">
            <h2 className="text-xl font-semibold text-[#f8f3ea]">
              {lang === 'en' ? 'Aromaboxes (Discovery Sets)' : 'Аромобокси (Discovery Sets)'}
            </h2>
            <Link
              to="/catalog"
              search={aromoboxesCategoryId ? { category: aromoboxesCategoryId } : undefined}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#9d733a]"
            >
              {T.shopNow}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="w-full overflow-hidden">
            <AutoscrollSlider>
              {aromoboxes.map((product) => {
                const name = localizedName(lang, product.name_uk, product.name_en)
                return (
                  <Link
                    key={product.id}
                    to="/catalog/$productSlug"
                    params={{ productSlug: product.slug || String(product.id) }}
                    className="group block overflow-hidden border border-[#657086] bg-[#f8f3ea] w-[200px] md:w-[260px]"
                  >
                    {product.photo_url ? (
                      <img
                        src={product.photo_url}
                        alt={name}
                        className="h-56 w-full object-cover transition group-hover:scale-[1.02] md:h-72"
                      />
                    ) : (
                      <div className="flex h-56 w-full items-center justify-center bg-[#efe3d1] md:h-72">
                        <Package className="h-7 w-7 text-[#9d733a]" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="line-clamp-2 min-h-[40px] text-sm font-semibold text-[#211915]">{name}</p>
                      <p className="mt-2 text-sm font-bold text-[#9d733a]">{product.price_uah} UAH</p>
                    </div>
                  </Link>
                )
              })}
            </AutoscrollSlider>
          </div>
        </section>
      )}

      {newest.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 py-8 md:px-8">
          <div className="mb-4 flex items-end justify-between gap-4">
            <h2 className="text-xl font-semibold text-[#f8f3ea]">{T.mayparfumChannelTitle}</h2>
            <Link to="/catalog" className="inline-flex items-center gap-1 text-sm font-semibold text-[#9d733a]">
              {T.shopNow}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="w-full overflow-hidden">
            <AutoscrollSlider>
              {newest.map((product) => {
                const name = localizedName(lang, product.name_uk, product.name_en)
                return (
                  <Link
                    key={product.id}
                    to="/catalog/$productSlug"
                    params={{ productSlug: product.slug || String(product.id) }}
                    className="group block overflow-hidden border border-[#657086] bg-[#f8f3ea] w-[190px] md:w-[250px]"
                  >
                    {product.photo_url ? (
                      <img
                        src={product.photo_url}
                        alt={name}
                        className="h-56 w-full object-cover transition group-hover:scale-[1.02] md:h-72"
                      />
                    ) : (
                      <div className="flex h-56 w-full items-center justify-center bg-[#efe3d1] md:h-72">
                        <Package className="h-7 w-7 text-[#9d733a]" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="line-clamp-2 min-h-[40px] text-sm font-semibold text-[#211915]">{name}</p>
                      <p className="mt-2 text-sm font-bold text-[#9d733a]">{product.price_uah} UAH</p>
                    </div>
                  </Link>
                )
              })}
            </AutoscrollSlider>
          </div>
        </section>
      )}

      {/* FEATURES ALTERNATING GRID */}
      <section className="mx-auto max-w-6xl px-5 py-8 md:px-8 space-y-12">
        {/* Feature 01: Personalized Scent Choice */}
        <div className="grid gap-6 md:grid-cols-2 items-center border border-[#657086] bg-[#252c38] overflow-hidden">
          <Link
            to="/quiz"
            className="block overflow-hidden group h-64 md:h-80"
          >
            <img
              src="/app/assets/personalized_banner.png"
              alt="Personalized Scent Selection"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
          </Link>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d7b171]">
              01
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-white">
              {lang === 'en' ? 'Personalized Scent Selection' : 'Персоналізований вибір ароматів'}
            </h2>
            <p className="mt-4 text-sm leading-6 text-[#b9aa99]">
              {lang === 'en'
                ? 'We offer an individual approach to perfume selection. We will help you choose a wardrobe of ideal fragrances matching your style and preferences.'
                : 'Ми пропонуємо індивідуальний підхід до вибору парфумерії. Ми допоможемо підібрати гардероб ідеальних ароматів, що відповідають вашому стилю та уподобанням.'}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/scent-finder"
                search={{ tab: 'capsule' }}
                className="inline-flex items-center gap-2 rounded-full bg-[#bc9050] px-5 py-2.5 text-xs font-semibold text-[#17120f] hover:bg-[#bc9050]/90 transition"
              >
                <span>{lang === 'en' ? '💼 Capsule Wardrobe 4×5ml (-15%)' : '💼 Парфумерний Гардероб 4×5 мл (-15%)'}</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/scent-finder"
                search={{ tab: 'compass' }}
                className="inline-flex items-center gap-2 rounded-full border border-[#bc9050]/60 px-4 py-2.5 text-xs font-semibold text-[#bc9050] hover:bg-[#bc9050]/10 transition"
              >
                <span>{lang === 'en' ? '🧭 Scent Compass' : '🧭 Компас Ароматів'}</span>
              </Link>
              <Link
                to="/quiz"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-xs font-semibold text-white/80 hover:bg-white/10 transition"
              >
                <span>{lang === 'en' ? 'Quick Quiz' : 'Квіз'}</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Feature 02: YouTube Reviews */}
        <div className="border border-[#657086] bg-[#252c38] p-6 md:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d7b171]">02</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {lang === 'en' ? 'Video Reviews' : 'Відеоогляди'}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#b9aa99]">
            {lang === 'en'
              ? 'We make our own video reviews of our perfumes or collections for a more comfortable dive into the world of scents.'
              : 'Ми робимо власні відеоогляди наших парфумів або колекцій для більш зручного занурення у світ ароматів.'}
          </p>
          {settings?.youtube_channel_id ? (
            <div className="mt-6">
              <YouTubeCarousel channelId={settings.youtube_channel_id} />
            </div>
          ) : null}
          <div className="mt-6">
            <a
              href="https://www.youtube.com/@MAY_PARFUM"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#bc9050] px-5 py-2.5 text-xs font-semibold text-[#17120f] hover:bg-[#bc9050]/90 transition"
            >
              {lang === 'en' ? 'Watch Reviews' : 'Дивитися огляди'}
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* PERFUME CULTURE ARTICLES SECTION */}
      {(() => {
        const featuredSlugs = new Set([
          'how-to-choose-niche-perfume',
          'what-is-decant',
          'perfume-longevity-and-sillage',
        ])
        const featuredArticles = (pages ?? []).filter((p) => featuredSlugs.has(p.slug))
        if (featuredArticles.length === 0) return null

        return (
          <section id="stories" className="mx-auto max-w-6xl px-5 py-8 md:px-8">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d7b171]">
                  {lang === 'en' ? 'PERFUME CULTURE' : 'ПАРФУМЕРНА КУЛЬТУРА'}
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-white">
                  {lang === 'en' ? 'Guides & Articles' : 'Корисні гайди та статті'}
                </h2>
              </div>
              <Link to="/stories" className="inline-flex items-center gap-1 text-sm font-semibold text-[#9d733a]">
                {lang === 'en' ? 'Show all' : 'Показати всі'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid gap-6 grid-cols-1 md:grid-cols-3">
              {featuredArticles.map((article) => {
                const title = localizedName(lang, article.title_uk, article.title_en)
                const content = localizedName(lang, article.content_uk, article.content_en) || ''
                // Simple regex block stripper for preview
                const cleanContent = content.replace(/[#*_\n\r]+/g, ' ').replace(/\s+/g, ' ').trim()
                
                return (
                  <Link
                    key={article.slug}
                    to="/pages/$slug"
                    params={{ slug: article.slug }}
                    className="group flex flex-col justify-between border border-[#657086] bg-[#252c38] p-6 transition hover:bg-[#efe3d1]/5"
                  >
                    <div>
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#bc9050]/10 text-[#d7b171] mb-4">
                        <BookOpenText className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-semibold text-white group-hover:text-[#d7b171] transition">
                        {title}
                      </h3>
                      <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-[#b9aa99]">
                        {cleanContent}
                      </p>
                    </div>
                    <div className="mt-6 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#d7b171]">
                      {lang === 'en' ? 'Read article' : 'Читати статтю'}
                      <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                    </div>
                  </Link>
                )
              })}
            </div>
          </section>
        )
      })()}

      {highlights.length > 0 && (
        <section className="mx-auto grid max-w-6xl gap-3 px-5 py-8 md:grid-cols-3 md:px-8">
          {highlights.map((item, index) => {
            const Icon = index === 0 ? ShieldCheck : index === 1 ? Gem : BookOpenText
            return (
              <div key={item.title} className="border border-[#657086] bg-[#f8f3ea] p-4">
                <Icon className="h-5 w-5 text-[#9d733a]" />
                <h3 className="mt-3 text-base font-semibold text-[#211915]">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-[#5b5147]">{item.body}</p>
              </div>
            )
          })}
        </section>
      )}



      {pages && pages.length > 0 && (
        <div className="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-2 border-t border-gray-100 px-4 pt-4">
          {pages.map((page) => (
            <Link
              key={page.slug}
              to="/pages/$slug"
              params={{ slug: page.slug }}
              className="text-xs text-gray-400"
            >
              {localizedName(lang, page.title_uk, page.title_en)}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
